import { useEffect, useRef } from "react";

/**
 * Stack of escape-key handlers. The last registered handler is the "topmost"
 * and is the only one that fires when Escape is pressed. This lets an
 * OverlayModal absorb the keypress without also closing the Global Modal
 * underneath it.
 */
const handlerStack = [];

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && handlerStack.length > 0) {
    const topHandler = handlerStack[handlerStack.length - 1];
    topHandler(event);
  }
});

/**
 * Register an Escape-key handler that only fires when it is the topmost
 * in the stack. Components mounted later (higher z-index overlays) take
 * priority over earlier ones.
 *
 * Uses a ref for the handler so the stack entry is stable — only `enabled`
 * controls push/pop. This prevents stale entries when the handler's identity
 * changes while enabled.
 *
 * @param {Function} handler - Called when Escape is pressed and this is the topmost handler.
 * @param {boolean} enabled - When false the handler is not registered.
 */
const useEscapeKey = (handler, enabled = true) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const stableHandler = (event) => handlerRef.current(event);
    handlerStack.push(stableHandler);

    return () => {
      const index = handlerStack.indexOf(stableHandler);
      if (index !== -1) handlerStack.splice(index, 1);
    };
  }, [enabled]);
};

export default useEscapeKey;
